import React, { useState } from 'react';
import { getSignalLabel, MonitorSignalRule, formatCondition } from '../../services/klineTypes';
import { klineMonitorService, MonitorTask, MonitorSignal } from '../../services/klineMonitorService';
import { useToastStore } from '../../stores/useToastStore';
import { OrderSide, ORDER_SIDE } from '@/services/enums';

interface SignalItemProps {
  symbol: string;
  signal: MonitorSignalRule & {
    triggered?: boolean;
    triggeredAt?: string;
    type: OrderSide;
    conditions?: any[];
  };
  monitorData: MonitorTask | null;
}

const checkConditionStatus = (condition: any) => {
  return condition.met;
};

const SignalItem: React.FC<SignalItemProps> = ({ symbol, signal, monitorData }) => {
  const showToast = useToastStore((state) => state.show);
  const [loading, setLoading] = useState(false);
  const signalTypeLabel = getSignalLabel(signal.signalType);

  const handleSendSignal = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const price = monitorData?.currentData?.price || 0;
      // Ensure signal object matches MonitorSignal interface required by sendAutoTradingSignal
      const signalToSend = {
        ...signal,
        triggered: signal.triggered ?? false,
        conditions: signal.conditions || [],
      } as MonitorSignal;

      await klineMonitorService.sendAutoTradingSignal(symbol, price, signalToSend);
      showToast('交易信号已发送', 'success');
    } catch (error: any) {
      console.error('Failed to execute signal:', error);
      showToast(error.message || '发送信号失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`p-4 rounded-lg border ${
        signal.triggered
          ? signal.type === ORDER_SIDE.BUY
            ? 'bg-green-500/20 border-green-500/40'
            : 'bg-red-500/20 border-red-500/40'
          : 'bg-white/5 border-white/10'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-white">
            {signal.type === ORDER_SIDE.BUY ? '🟢 做多信号' : '🔴 做空信号'}
          </div>
          {signalTypeLabel && (
            <div
              className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30 whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px]"
              title={signalTypeLabel}
            >
              {signalTypeLabel}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* 做多/做空按钮 */}
          {monitorData?.currentData?.price && (
            <button
              onClick={handleSendSignal}
              disabled={loading}
              className={`px-3 py-1 text-xs rounded border transition-colors flex items-center gap-1 outline-none focus:outline-none ${
                signal.type === ORDER_SIDE.BUY
                  ? 'bg-green-500/20 text-green-200 border-green-500/40 hover:bg-green-500/30 active:bg-green-500/30 focus:bg-green-500/30 disabled:bg-green-500/20 disabled:text-green-200'
                  : 'bg-red-500/20 text-red-200 border-red-500/40 hover:bg-red-500/30 active:bg-red-500/30 focus:bg-red-500/30 disabled:bg-red-500/20 disabled:text-red-200'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>处理中...</span>
                </>
              ) : (
                <span>{signal.type === ORDER_SIDE.BUY ? '做多' : '做空'}</span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* 止盈止损价格显示 */}
      {(signal.takeProfitPrice || signal.stopLossPrice || signal.triggered) && (
        <div className="mb-3 p-2 bg-white/5 rounded border border-white/10">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs text-white/60 font-medium">
              {signal.takeProfitPrice || signal.stopLossPrice ? '目标价格' : ''}
            </div>
            {signal.triggered && (
              <div className="text-xs font-semibold text-yellow-400 animate-pulse">
                已触发 {signal.triggeredAt ? new Date(signal.triggeredAt).toLocaleTimeString('zh-CN') : ''}
              </div>
            )}
          </div>
          {(signal.takeProfitPrice || signal.stopLossPrice) && (
            <div className="flex items-center gap-4 text-xs">
              {signal.takeProfitPrice && (
                <div className="flex items-center gap-1.5">
                  <span className="text-white/60">止盈:</span>
                  <span className="text-green-400 font-semibold">{signal.takeProfitPrice}</span>
                </div>
              )}
              {signal.stopLossPrice && (
                <div className="flex items-center gap-1.5">
                  <span className="text-white/60">止损:</span>
                  <span className="text-red-400 font-semibold">{signal.stopLossPrice}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="text-xs text-white/70 mb-2">{signal.description}</div>
      {signal.conditions && signal.conditions.length > 0 ? (
        <div className="mt-2 space-y-1">
          <div className="text-xs text-white/60 mb-1">监控条件：</div>
          {signal.conditions.map((condition: any, condIndex: number) => {
            const conditionText = formatCondition(condition);
            const conditionStatus = checkConditionStatus(condition);
            const currentValue = condition.currentValue;
            // 获取 EMA 值：优先从 condition 中获取，如果没有则从 monitorData.currentData 中获取
            const ema20 = condition.ema20 ?? monitorData?.currentData?.ema20;

            return (
              <div key={condIndex} className="flex items-center justify-between text-xs pl-2">
                <div className="text-white/60">{conditionText}</div>
                <div className="flex items-center gap-2">
                  {/* 只在 price、high、low、close 条件前面显示 EMA20 */}
                  {(condition.field === 'price' ||
                    condition.field === 'high' ||
                    condition.field === 'low' ||
                    condition.field === 'close') &&
                    ema20 != null &&
                    typeof ema20 === 'number' &&
                    !isNaN(ema20) && <div className="text-white/40 text-xs">EMA20: {ema20}</div>}
                  {/* 显示当前值 - 优先显示后端返回的 currentValue */}
                  {currentValue != null && (
                    <>
                      {typeof currentValue === 'number' && !isNaN(currentValue) && (
                        <div
                          className={`text-xs ${
                            conditionStatus === true
                              ? 'text-green-400'
                              : conditionStatus === false
                                ? 'text-red-400'
                                : 'text-white/40'
                          }`}
                        >
                          当前:{' '}
                          {(() => {
                            return currentValue;
                          })()}
                        </div>
                      )}
                      {typeof currentValue === 'boolean' && (
                        <div
                          className={`text-xs ${
                            conditionStatus === true
                              ? 'text-green-400'
                              : conditionStatus === false
                                ? 'text-red-400'
                                : 'text-white/40'
                          }`}
                        >
                          {currentValue ? '✓' : '✗'}
                        </div>
                      )}
                    </>
                  )}
                  {/* 如果没有 currentValue，尝试从 indicators 中获取并显示（作为备用显示） */}
                  {currentValue == null && monitorData?.currentData && (
                    <>
                      {(condition.field === 'macd' ||
                        condition.field === 'macd_cross' ||
                        condition.field === 'macdCross') &&
                        monitorData.currentData.macd?.macd != null &&
                        !isNaN(monitorData.currentData.macd.macd) && (
                          <div className="text-xs text-white/40">当前: {monitorData.currentData.macd.macd}</div>
                        )}
                      {(condition.field === 'macd_signal' ||
                        condition.field === 'macd_cross' ||
                        condition.field === 'macdCross') &&
                        monitorData.currentData.macd?.signal != null &&
                        !isNaN(monitorData.currentData.macd.signal) && (
                          <div className="text-xs text-white/40">当前: {monitorData.currentData.macd.signal}</div>
                        )}
                      {(condition.field === 'macd_histogram' || condition.field === 'macdHistogram') &&
                        monitorData.currentData.macd?.histogram != null &&
                        !isNaN(monitorData.currentData.macd.histogram) && (
                          <div className="text-xs text-white/40">当前: {monitorData.currentData.macd.histogram}</div>
                        )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-xs text-white/40 mt-2">无监控条件</div>
      )}
      {/* 显示信号级别的 confidence 字段 - 放在监控条件下方，label与"监控条件："左对齐 */}
      {signal.confidence != null && typeof signal.confidence === 'number' && (
        <div className="flex items-center justify-between text-xs mt-2">
          <div className="text-white/60">置信度：</div>
          <div className="text-blue-400/70">{signal.confidence}%</div>
        </div>
      )}
    </div>
  );
};

export default SignalItem;
